import { z } from 'zod'
import { decimalSchema, timestampSchema, uuidSchema } from './common.ts'
import { productionPlanMonthSchema } from './production-plan.ts'
import { productionQuantitySchema } from './production-stages.ts'
import { warehouseUnitCodeSchema } from './units.ts'

export const productionProgressBasisSchema = z.enum(['stages', 'finished_quantity'])
export type ProductionProgressBasis = z.infer<typeof productionProgressBasisSchema>

export const productionOutputKindSchema = z.enum(['completion', 'adjustment'])
export type ProductionOutputKind = z.infer<typeof productionOutputKindSchema>

const isZeroDecimal = (value: string): boolean => /^-?0+(?:\.0+)?$/.test(value)

export const productionOutputEntrySchema = z.object({
  id: uuidSchema,
  requestId: uuidSchema,
  orderId: uuidSchema,
  kind: productionOutputKindSchema,
  quantity: decimalSchema,
  unitCode: z.string(),
  unitSymbol: z.string(),
  unitPrecision: z.number().int().min(0).max(3),
  productionDate: z.iso.date(),
  reason: z.string().nullable(),
  createdBy: z.string(),
  createdByName: z.string().nullable(),
  createdAt: timestampSchema,
})
export type ProductionOutputEntry = z.infer<typeof productionOutputEntrySchema>

export interface ProductionOutputRecordResult {
  entry: ProductionOutputEntry
  order: import('./production-stages.ts').ProductionOrder
}

export const recordProductionOutputInputSchema = z
  .strictObject({
    requestId: uuidSchema,
    kind: productionOutputKindSchema.default('completion'),
    quantity: decimalSchema,
    productionDate: z.iso.date(),
    reason: z.string().trim().min(1).max(1000).nullable().default(null),
  })
  .superRefine((input, context) => {
    if (
      input.kind === 'completion' &&
      (input.quantity.startsWith('-') || isZeroDecimal(input.quantity))
    )
      context.addIssue({ code: 'custom', path: ['quantity'], message: 'Вкажіть додатну кількість' })
    if (input.kind === 'completion' && input.reason !== null)
      context.addIssue({
        code: 'custom',
        path: ['reason'],
        message: 'Причина потрібна лише для коригування',
      })
    if (input.kind === 'adjustment' && input.reason === null)
      context.addIssue({ code: 'custom', path: ['reason'], message: 'Вкажіть причину коригування' })
    if (isZeroDecimal(input.quantity))
      context.addIssue({
        code: 'custom',
        path: ['quantity'],
        message: 'Кількість не може дорівнювати нулю',
      })
  })
export type RecordProductionOutputInput = z.infer<typeof recordProductionOutputInputSchema>

export const updateProductionProgressInputSchema = z
  .strictObject({
    progressBasis: productionProgressBasisSchema,
    targetQuantity: productionQuantitySchema.nullable().default(null),
    unitCode: warehouseUnitCodeSchema.nullable().default(null),
  })
  .superRefine((input, context) => {
    if (input.progressBasis === 'finished_quantity' && (!input.targetQuantity || !input.unitCode))
      context.addIssue({
        code: 'custom',
        path: ['targetQuantity'],
        message: 'Для прогресу за кількістю вкажіть план та одиницю',
      })
    if ((input.targetQuantity === null) !== (input.unitCode === null))
      context.addIssue({
        code: 'custom',
        path: ['unitCode'],
        message: 'Кількість та одиницю потрібно вказати разом',
      })
  })
export type UpdateProductionProgressInput = z.infer<typeof updateProductionProgressInputSchema>

export const productionOperationalPlanQuerySchema = z.strictObject({
  month: productionPlanMonthSchema,
  /**
   * Comma-separated order ids. Given, the plan lists exactly these orders, wherever their
   * deadline now falls, with progress as of the month's end. «План виробництва» reads its rows'
   * progress this way: a plan row stays in its planned month when the order's deadline moves.
   */
  orderIds: z.preprocess(
    // Present but empty is a mistake, not «no filter»: it fails the id check.
    (value) => (typeof value === 'string' ? value.split(',') : value),
    z.array(uuidSchema).min(1).max(200).optional(),
  ),
})
export type ProductionOperationalPlanQuery = z.infer<typeof productionOperationalPlanQuerySchema>

export const productionOperationalPlanRowSchema = z.object({
  orderId: uuidSchema,
  orderNumber: z.string(),
  productName: z.string(),
  kind: z.enum(['customer', 'internal']),
  sourceName: z.string().nullable(),
  deadline: timestampSchema.nullable(),
  workCenterName: z.string().nullable(),
  progressBasis: productionProgressBasisSchema,
  plannedQuantity: decimalSchema.nullable(),
  unitSymbol: z.string().nullable(),
  completedQuantity: decimalSchema,
  completedStages: z.number().int().nonnegative(),
  totalStages: z.number().int().nonnegative(),
  progressPercent: z.number().int().min(0).max(100),
})
export type ProductionOperationalPlanRow = z.infer<typeof productionOperationalPlanRowSchema>

export const productionOperationalPlanSchema = z.object({
  rows: z.array(productionOperationalPlanRowSchema),
  plannedOrders: z.number().int().nonnegative(),
  completedOrders: z.number().int().nonnegative(),
  inProgressOrders: z.number().int().nonnegative(),
  needsTargetOrders: z.number().int().nonnegative(),
})
export type ProductionOperationalPlan = z.infer<typeof productionOperationalPlanSchema>
